import Select from 'react-select'
import { useCallback } from 'react'
import { useTranslations } from 'next-intl'
import type { CustomCellEditorProps } from 'ag-grid-react'
import type { GroupBase } from 'react-select'
import { MapPinPlusIcon } from '@phosphor-icons/react/dist/ssr/MapPinPlus'
import type { Place } from '@skop-app/lib-domain/types'
import type { ReactNode } from 'react'

import type { Context } from '@/types/grid'
import type { AdaptedProduct } from '@/services/property'
import { useSelectEditor } from '../useSelectEditor'
import { openModal } from '@/components/modals/modal.registry.events'
import type { UpsertProductVariables } from '@/services/mutations'
import {
  PLACE_CREATE_SENTINEL,
  type PlaceOptionGroup,
  type PlacePickerOption,
} from '@/services/place/place.utils'

/** Place option from context may include organizationID for filtering */
type CreateNewOption = {
  value: typeof PLACE_CREATE_SENTINEL
  label: ReactNode
}

type PlaceSelectOption = PlacePickerOption | CreateNewOption
type PlaceSelectGroup = GroupBase<PlaceSelectOption> & {
  label: string
  options: PlaceSelectOption[]
}

function isPlaceOptionForOrganization(
  place: PlacePickerOption,
  organizationID?: string,
) {
  return (
    typeof organizationID === 'undefined' ||
    place.organizationID === organizationID
  )
}

function isCreateNewOption(
  opt: PlaceSelectOption | null,
): opt is CreateNewOption {
  return opt !== null && 'value' in opt && opt.value === PLACE_CREATE_SENTINEL
}

const filterPlaceGroupsForOrganization = (
  groups: PlaceOptionGroup[] | undefined,
  organizationID?: string,
): PlaceSelectGroup[] =>
  (groups ?? [])
    .map((group) => ({
      ...group,
      options: (group.options ?? []).filter((place) =>
        isPlaceOptionForOrganization(place, organizationID),
      ),
    }))
    .filter((group) => group.options.length > 0)

export function SelectPlaceEditor({
  onValueChange,
  stopEditing,
  colDef,
  eGridCell,
  context,
  data,
}: CustomCellEditorProps<AdaptedProduct, unknown, Context>) {
  const t = useTranslations('Toolbar')

  const { field, baseSelectProps } = useSelectEditor({ eGridCell, colDef })

  const isMaster = context?.authorization?.user?.isMaster
  const organizationID = context?.ownership?.organizationID
  const isOrganizationView = context?.isOrganizationView
  const currentPlaces: PlacePickerOption[] =
    (!isMaster && organizationID) || isOrganizationView
      ? (context?.placeID ?? []).filter((place): place is PlacePickerOption =>
          isPlaceOptionForOrganization(place, organizationID),
        )
      : (context?.placeID ?? [])
  const currentGroupedPlaces =
    (!isMaster && organizationID) || isOrganizationView
      ? filterPlaceGroupsForOrganization(
          context?.groupedPlaceID,
          organizationID,
        )
      : ((context?.groupedPlaceID ?? []) as PlaceSelectGroup[])

  const value = data?.[field as keyof AdaptedProduct]
  const createOption: CreateNewOption = {
    value: PLACE_CREATE_SENTINEL,
    label: (
      <div className="flex items-center gap-2">
        <MapPinPlusIcon className="size-4" />
        <span className="text-primary">{t('add-place')}</span>
      </div>
    ),
  }
  const options: Array<PlaceSelectOption | PlaceSelectGroup> =
    currentGroupedPlaces.length > 0
      ? [...currentGroupedPlaces, createOption]
      : [...currentPlaces, createOption]

  const onChange = useCallback(
    (event: PlaceSelectOption | null) => {
      if (!event) {
        stopEditing()
        return
      }
      if (isCreateNewOption(event)) {
        openModal('editPlace', {
          onSaved: (place?: Place) => {
            if (
              place?.id &&
              data?.id &&
              data?.productID &&
              context?.ownership
            ) {
              const {
                byUserID,
                updatedBy: _ignoreUpdatedBy,
                ...restOwnership
              } = context.ownership
              const updatedBy =
                context.authorization?.user?.id ??
                context.ownership.updatedBy ??
                byUserID

              const payload: UpsertProductVariables = {
                upsertType: 'update',
                input: {
                  id: data.id,
                  productID: data.productID,
                  progress: 'active',
                  placeID: place.id,
                  ...(place.location ? { location: place.location } : {}),
                  updatedAt: new Date().toISOString(),
                  updatedBy,
                  ...restOwnership,
                },
              }
              context.upsertProduct?.(payload)
            }
            stopEditing()
          },
        })
        return
      }
      onValueChange(event)
      stopEditing()
    },
    [context, data, onValueChange, stopEditing],
  )

  return (
    <Select<PlaceSelectOption, false, PlaceSelectGroup>
      {...baseSelectProps}
      filterOption={(candidate, inputValue) => {
        if (!inputValue.trim()) return true
        if (candidate.data.value === PLACE_CREATE_SENTINEL) return true

        const searchTarget =
          'searchText' in candidate.data
            ? (candidate.data.searchText ?? String(candidate.label ?? ''))
            : String(candidate.label ?? '')

        return searchTarget
          .toLocaleLowerCase()
          .includes(inputValue.trim().toLocaleLowerCase())
      }}
      formatOptionLabel={(option, meta) => {
        if (isCreateNewOption(option)) return option.label
        if (meta.context === 'value') return option.label

        return (
          <div className="flex flex-col">
            <span>{option.label}</span>
            {option.subtitle ? (
              <span className="text-xs text-slate-500">{option.subtitle}</span>
            ) : null}
          </div>
        )
      }}
      onChange={onChange}
      defaultValue={value as PlaceSelectOption}
      options={options}
      placeholder={t('select-option')}
      noOptionsMessage={() => t('no-options')}
      closeMenuOnSelect
      openMenuOnClick
    />
  )
}
